import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCategories } from "@/contexts/CategoriesContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";
import { fmtCurrency, fmtDate, daysUntil } from "@/lib/format";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";
import UpsellCard from "@/components/UpsellCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Target, Plus, Pencil, Trash2, PiggyBank, CalendarDays, LayoutGrid, List as ListIcon, ArrowRight, Minus, Users, User as UserIcon } from "lucide-react";

const PALETTE = ["#4F46E5","#10B981","#0EA5E9","#F59E0B","#EF4444","#8B5CF6"];

export default function SinkingFunds() {
    const { user } = useAuth();
    const cur = user?.currency || "USD";
    const navigate = useNavigate();
    const { isMember, memberName } = useHousehold();
    const canView = hasProAccess(user, isMember);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // Default view: grid/cards on mobile (< sm), list on sm+ — desktop behavior unchanged.
    // Only sets the initial default; the toggle above still switches freely either way.
    const [view, setView] = useState(() => (typeof window !== "undefined" && window.innerWidth < 640 ? "cards" : "list"));
    // Opens with the user's preferred Combined/Just me default (Settings → Preferences).
    const [scope, setScope] = useState(() => user?.default_scope || "mine");
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [contribFund, setContribFund] = useState(null);

    const load = async () => {
        if (!canView) { setLoading(false); return; }
        setLoading(true);
        try {
            const params = {};
            if (isMember) params.scope = scope;
            const { data } = await api.get("/funds", { params });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, isMember, canView]);

    const onDelete = async () => {
        if (!deleteId) return;
        try {
            await api.delete(`/funds/${deleteId}`);
            toast.success("Fund removed");
            setItems(items.filter(i => i.id !== deleteId));
        } catch { toast.error("Failed to delete"); }
        finally { setDeleteId(null); }
    };

    if (!canView) {
        return (
            <div className="space-y-4" data-testid="funds-page">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Sinking funds</h1>
                    <p className="text-sm text-slate-500 mt-1">Save toward goals — one drop at a time.</p>
                </div>
                <UpsellCard
                    feature="Sinking Funds"
                    description="Upgrade to Pro to save toward goals with progress tracking, deposits, and withdrawals."
                />
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="funds-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Sinking funds</h1>
                    <p className="text-sm text-slate-500 mt-1">Save toward goals — one drop at a time.</p>
                </div>
                <div className="flex items-center gap-2">
                    {isMember ? (
                        <Tabs value={scope} onValueChange={setScope}>
                            <TabsList className="bg-slate-100 rounded-xl p-1">
                                <TabsTrigger data-testid="funds-scope-household" value="household" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <Users className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Combined</span>
                                </TabsTrigger>
                                <TabsTrigger data-testid="funds-scope-mine" value="mine" className="rounded-lg data-[state=active]:bg-white gap-1.5">
                                    <UserIcon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Just me</span>
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    ) : null}
                    <Tabs value={view} onValueChange={setView}>
                        <TabsList className="bg-slate-100 rounded-xl p-1">
                            <TabsTrigger data-testid="funds-view-cards" value="cards" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <LayoutGrid className="w-4 h-4" />
                            </TabsTrigger>
                            <TabsTrigger data-testid="funds-view-list" value="list" className="rounded-lg data-[state=active]:bg-white px-2.5">
                                <ListIcon className="w-4 h-4" />
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <Button onClick={() => setEditing({ _new: true, current_amount: "0", category: "General" })} data-testid="new-fund-btn" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl gap-1.5">
                        <Plus className="w-4 h-4" /> New goal
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="card-elev rounded-2xl p-10 text-center text-slate-400 text-sm">Loading…</div>
            ) : items.length === 0 ? (
                <div className="card-elev rounded-2xl">
                    <EmptyState icon={Target} title="No goals yet" description="Create your first sinking fund to start saving."
                        action={<Button onClick={() => setEditing({ _new: true, current_amount: "0", category: "General" })} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"><Plus className="w-4 h-4 mr-1.5" /> New goal</Button>}
                    />
                </div>
            ) : view === "cards" ? (
                // Grid/cards view — available on mobile too (1 column, no horizontal scroll needed).
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="funds-grid">
                    {items.map((f, i) => {
                        const goal = parseFloat(f.goal_amount) || 0;
                        const cur_amt = parseFloat(f.current_amount) || 0;
                        const pct = goal > 0 ? Math.min(100, (cur_amt / goal) * 100) : 0;
                        const color = f.color || PALETTE[i % PALETTE.length];
                        const dleft = f.due_date ? daysUntil(f.due_date) : null;
                        const radius = 36;
                        const circ = 2 * Math.PI * radius;
                        const dash = (pct / 100) * circ;
                        return (
                            <div key={f.id} data-testid={`fund-${f.id}`} className="card-elev rounded-2xl p-6 group">
                                <Link to={`/funds/${f.id}`} data-testid={`fund-open-${f.id}`} className="flex items-start gap-4 mb-4 -m-1 p-1 rounded-xl hover:bg-slate-50/60 transition-colors cursor-pointer">
                                    <div className="relative w-20 h-20 flex-shrink-0">
                                        <svg viewBox="0 0 90 90" className="w-full h-full -rotate-90">
                                            <circle cx="45" cy="45" r={radius} fill="none" stroke="#F1F5F9" strokeWidth="9" />
                                            <circle cx="45" cy="45" r={radius} fill="none" stroke={color} strokeWidth="9" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} className="transition-all duration-500" />
                                        </svg>
                                        <div className="absolute inset-0 grid place-items-center">
                                            <span className="font-display text-base font-extrabold text-slate-900">{Math.round(pct)}%</span>
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <h3 className="font-display text-lg font-bold text-slate-900 truncate group-hover:text-indigo-700 transition-colors">{f.name}</h3>
                                            <ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                                            {isMember && scope === "household" && f.user_id !== user.id ? (
                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(f.user_id)}</span>
                                            ) : null}
                                        </div>
                                        <p className="text-xs text-slate-500 mb-1">{f.category}</p>
                                        <div className="font-display text-xl font-extrabold text-slate-900 tabular-nums">{fmtCurrency(cur_amt, cur)}</div>
                                        <div className="text-xs text-slate-500">of {fmtCurrency(goal, cur)} goal</div>
                                    </div>
                                </Link>
                                {f.due_date ? (
                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
                                        <CalendarDays className="w-3.5 h-3.5" />
                                        Due {fmtDate(f.due_date)}{dleft !== null ? ` · ${dleft >= 0 ? `${dleft}d left` : "Past due"}` : ""}
                                    </div>
                                ) : null}
                                {f.user_id === user.id ? (
                                    <div className="flex gap-2 pt-3 border-t border-slate-100">
                                        <Button onClick={() => setContribFund({ ...f, _kind: "deposit" })} data-testid={`contrib-${f.id}`} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl gap-1.5 h-9">
                                            <PiggyBank className="w-4 h-4" /> Contribute
                                        </Button>
                                        <Button onClick={() => setContribFund({ ...f, _kind: "withdrawal" })} data-testid={`withdraw-${f.id}`} variant="outline" className="rounded-xl gap-1.5 h-9 px-3 border-slate-200 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200">
                                            <Minus className="w-4 h-4" /> Withdraw
                                        </Button>
                                        <button onClick={() => setEditing({ ...f, due_date: f.due_date || "" })} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                        <button onClick={() => setDeleteId(f.id)} className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                    </div>
                                ) : (
                                    <div className="pt-3 border-t border-slate-100 text-xs text-slate-400">{memberName(f.user_id)}'s goal</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <>
                {/* Mobile: compact stacked list, no horizontal scroll */}
                <div className="lg:hidden card-elev rounded-2xl divide-y divide-slate-100 overflow-hidden" data-testid="funds-mobile-list">
                    {items.map((f, i) => {
                        const goal = parseFloat(f.goal_amount) || 0;
                        const cur_amt = parseFloat(f.current_amount) || 0;
                        const pct = goal > 0 ? Math.min(100, (cur_amt / goal) * 100) : 0;
                        const color = f.color || PALETTE[i % PALETTE.length];
                        const dleft = f.due_date ? daysUntil(f.due_date) : null;
                        return (
                            <div key={f.id} data-testid={`m-fund-${f.id}`} onClick={() => navigate(`/funds/${f.id}`)} className="flex items-center gap-2.5 px-3 py-2.5 active:bg-slate-50/60 transition-colors cursor-pointer">
                                <span className="w-9 h-9 rounded-lg grid place-items-center flex-shrink-0" style={{ backgroundColor: `${color}18`, color }}>
                                    <PiggyBank className="w-4 h-4" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="font-semibold text-sm text-slate-900 truncate flex items-center gap-1.5">
                                            {f.name}
                                            {isMember && scope === "household" && f.user_id !== user.id ? (
                                                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(f.user_id)}</span>
                                            ) : null}
                                        </span>
                                        <span className="font-display font-bold text-xs text-slate-900 tabular-nums flex-shrink-0">
                                            {fmtCurrency(cur_amt, cur)}<span className="text-slate-400 font-normal">/{fmtCurrency(goal, cur)}</span>
                                        </span>
                                    </div>
                                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
                                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                    </div>
                                    <div className="flex items-center justify-between mt-1 text-[11px]">
                                        <span className="text-slate-500">{f.category}</span>
                                        <span className="text-slate-400 flex-shrink-0">
                                            {Math.round(pct)}%{f.due_date ? ` · ${dleft >= 0 ? `${dleft}d left` : "Past due"}` : ""}
                                        </span>
                                    </div>
                                </div>
                                {f.user_id === user.id ? (
                                    <div className="flex flex-col gap-0.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                        <button onClick={() => setContribFund({ ...f, _kind: "deposit" })} data-testid={`m-fund-contrib-${f.id}`} className="p-1 rounded-md text-emerald-600 hover:bg-emerald-50">
                                            <PiggyBank className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => setEditing({ ...f, due_date: f.due_date || "" })} data-testid={`m-fund-edit-${f.id}`} className="p-1 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                                            <Pencil className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>

                {/* Desktop (lg+): unchanged table */}
                <div className="hidden lg:block card-elev rounded-2xl overflow-hidden" data-testid="funds-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50/70 text-slate-500 text-xs uppercase tracking-wider">
                                <tr>
                                    <th className="text-left font-semibold px-5 py-3">Goal</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden md:table-cell">Category</th>
                                    <th className="text-left font-semibold px-5 py-3 hidden lg:table-cell">Due</th>
                                    <th className="text-left font-semibold px-5 py-3 min-w-[180px]">Progress</th>
                                    <th className="text-right font-semibold px-5 py-3">Saved</th>
                                    <th className="text-right font-semibold px-5 py-3 hidden md:table-cell">Goal</th>
                                    <th className="text-right font-semibold px-5 py-3 w-56"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((f, i) => {
                                    const goal = parseFloat(f.goal_amount) || 0;
                                    const cur_amt = parseFloat(f.current_amount) || 0;
                                    const pct = goal > 0 ? Math.min(100, (cur_amt / goal) * 100) : 0;
                                    const color = f.color || PALETTE[i % PALETTE.length];
                                    const dleft = f.due_date ? daysUntil(f.due_date) : null;
                                    return (
                                        <tr key={f.id} data-testid={`fund-row-${f.id}`} onClick={() => navigate(`/funds/${f.id}`)} className="hover:bg-slate-50/50 transition-colors cursor-pointer">
                                            <td className="px-5 py-3.5">
                                                <div className="font-semibold text-slate-900 inline-flex items-center gap-1.5 group">
                                                    {f.name}
                                                    <ArrowRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                                                    {isMember && scope === "household" && f.user_id !== user.id ? (
                                                        <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 text-indigo-600 shrink-0">{memberName(f.user_id)}</span>
                                                    ) : null}
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 hidden md:table-cell">
                                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-700">{f.category}</span>
                                            </td>
                                            <td className="px-5 py-3.5 hidden lg:table-cell text-slate-600">
                                                {f.due_date ? <>{fmtDate(f.due_date)}{dleft !== null ? <span className="block text-xs text-slate-400">{dleft >= 0 ? `${dleft}d left` : "Past due"}</span> : null}</> : <span className="text-slate-400">—</span>}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                                                    </div>
                                                    <span className="text-xs font-semibold text-slate-600 tabular-nums w-10 text-right">{Math.round(pct)}%</span>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 text-right font-display font-bold text-slate-900 tabular-nums">{fmtCurrency(cur_amt, cur)}</td>
                                            <td className="px-5 py-3.5 text-right text-slate-500 hidden md:table-cell tabular-nums">{fmtCurrency(goal, cur)}</td>
                                            <td className="px-5 py-3.5 text-right">
                                                <div className="inline-flex gap-1" onClick={e => e.stopPropagation()}>
                                                    {f.user_id === user.id ? (
                                                        <>
                                                            <button onClick={() => setContribFund({ ...f, _kind: "deposit" })} data-testid={`row-contrib-${f.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100">+ Add</button>
                                                            <button onClick={() => setContribFund({ ...f, _kind: "withdrawal" })} data-testid={`row-withdraw-${f.id}`} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-rose-50 text-rose-700 hover:bg-rose-100">− Withdraw</button>
                                                            <button onClick={() => setEditing({ ...f, due_date: f.due_date || "" })} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"><Pencil className="w-4 h-4" /></button>
                                                            <button onClick={() => setDeleteId(f.id)} className="p-1.5 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                                        </>
                                                    ) : (
                                                        <span className="text-xs text-slate-400">{memberName(f.user_id)}'s</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                </>
            )}

            <FundEditor item={editing} onClose={() => setEditing(null)} onSaved={load} />
            <ContributeDialog fund={contribFund} onClose={() => setContribFund(null)} onSaved={load} />

            <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this fund?</AlertDialogTitle>
                        <AlertDialogDescription>All contribution history for this fund will be lost.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function FundEditor({ item, onClose, onSaved }) {
    const { categoryNames } = useCategories();
    
    const [form, setForm] = useState(item || {});
    const [saving, setSaving] = useState(false);
    useEffect(() => { setForm(item || {}); }, [item]);
    if (!item) return null;

    const submit = async () => {
        if (!form.name || !form.goal_amount) { toast.error("Name and goal required"); return; }
        try {
            setSaving(true);
            const payload = {
                name: form.name, goal_amount: parseFloat(form.goal_amount),
                current_amount: parseFloat(form.current_amount || 0),
                category: form.category || "General", color: form.color || null,
                due_date: form.due_date || null, notes: form.notes || null,
            };
            if (form._new) await api.post("/funds", payload);
            else await api.patch(`/funds/${form.id}`, payload);
            toast.success(form._new ? "Fund created" : "Updated");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="fund-editor">
                <DialogHeader><DialogTitle className="font-display text-xl">{form._new ? "Create fund" : "Edit fund"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <FieldL label="Name"><Input data-testid="fe-name" value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} placeholder="Vacation 2026" /></FieldL>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Goal amount"><Input data-testid="fe-goal" type="number" step="0.01" value={form.goal_amount || ""} onChange={e => setForm({...form, goal_amount: e.target.value})} /></FieldL>
                        <FieldL label="Already saved"><Input data-testid="fe-current" type="number" step="0.01" value={form.current_amount || "0"} onChange={e => setForm({...form, current_amount: e.target.value})} /></FieldL>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <FieldL label="Category">
                            <Select value={form.category || "General"} onValueChange={v => setForm({...form, category: v})}>
                                <SelectTrigger data-testid="fe-cat"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {categoryNames.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </FieldL>
                        <FieldL label="Due date"><Input data-testid="fe-due" type="date" value={form.due_date || ""} onChange={e => setForm({...form, due_date: e.target.value})} /></FieldL>
                    </div>
                    <FieldL label="Notes"><Textarea data-testid="fe-notes" rows={2} value={form.notes || ""} onChange={e => setForm({...form, notes: e.target.value})} /></FieldL>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="fe-save" disabled={saving} onClick={submit} className="bg-indigo-600 hover:bg-indigo-700 text-white">{form._new ? "Create" : "Save"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ContributeDialog({ fund, onClose, onSaved }) {
    const [mode, setMode] = useState("deposit");
    const [amount, setAmount] = useState("");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);
    const { user } = useAuth();
    const cur = user?.currency || "USD";

    useEffect(() => {
        if (fund) {
            setAmount(""); setNote("");
            setMode(fund._kind === "withdrawal" ? "withdrawal" : "deposit");
            api.get(`/funds/${fund.id}/contributions`).then(({ data }) => setHistory(data));
        }
    }, [fund]);

    if (!fund) return null;

    const isWithdraw = mode === "withdrawal";
    const currentSaved = parseFloat(fund.current_amount) || 0;

    const submit = async () => {
        const amt = parseFloat(amount);
        if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
        if (isWithdraw && amt > currentSaved) {
            toast.error(`You only have ${fmtCurrency(currentSaved, cur)} saved`); return;
        }
        try {
            setSaving(true);
            await api.post(`/funds/${fund.id}/contributions`, { amount: amt, kind: mode, note: note || null });
            toast.success(isWithdraw ? "Withdrawal recorded" : "Contribution added");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={!!fund} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md rounded-2xl" data-testid="contribute-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">{fund.name}</DialogTitle>
                    <p className="text-xs text-slate-500">Currently saved: <span className="font-semibold text-slate-700 tabular-nums">{fmtCurrency(currentSaved, cur)}</span></p>
                </DialogHeader>
                <Tabs value={mode} onValueChange={setMode}>
                    <TabsList className="grid grid-cols-2 w-full" data-testid="contrib-mode">
                        <TabsTrigger value="deposit" data-testid="mode-deposit" className="data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700 gap-1.5">
                            <PiggyBank className="w-4 h-4" /> Contribute
                        </TabsTrigger>
                        <TabsTrigger value="withdrawal" data-testid="mode-withdraw" className="data-[state=active]:bg-rose-50 data-[state=active]:text-rose-700 gap-1.5">
                            <Minus className="w-4 h-4" /> Withdraw
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="space-y-3 pt-2">
                    <FieldL label={isWithdraw ? "Withdrawal amount" : "Contribution amount"}>
                        <Input data-testid="cd-amount" type="number" step="0.01" autoFocus value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
                    </FieldL>
                    <FieldL label="Note (optional)">
                        <Input data-testid="cd-note" value={note} onChange={e => setNote(e.target.value)} placeholder={isWithdraw ? "Used for emergency repair" : "Bonus"} />
                    </FieldL>
                </div>
                {history.length > 0 ? (
                    <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Recent activity</p>
                        <div className="space-y-1.5 max-h-40 overflow-y-auto">
                            {history.slice(0, 6).map(h => {
                                const isWd = h.kind === "withdrawal";
                                return (
                                    <div key={h.id} className="flex items-center justify-between text-sm">
                                        <span className="text-slate-600 truncate">{h.note || (isWd ? "Withdrawal" : "Contribution")} · <span className="text-xs text-slate-400">{fmtDate(h.occurred_at)}</span></span>
                                        <span className={`font-semibold tabular-nums ${isWd ? "text-rose-600" : "text-emerald-600"}`}>{isWd ? "−" : "+"}{fmtCurrency(h.amount, cur)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : null}
                <DialogFooter>
                    <Button variant="ghost" onClick={onClose}>Cancel</Button>
                    <Button data-testid="cd-save" disabled={saving} onClick={submit}
                        className={`text-white ${isWithdraw ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                        {isWithdraw ? "Record withdrawal" : "Save contribution"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldL({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}
